import { useState } from "react"
import { useNavigate, useParams, useSearchParams } from "react-router-dom"

import { Button, Form, Title } from "@/atoms"
import { RESOURCES_NAMES } from "@/const/resource.constants"
import { useCreate } from "@/hooks/useHttp"
import { useServices } from "@/hooks/useServices"
import { useTranslation } from "@/hooks/useTranslation"
import { ExpiredMessage } from "../../molecules/ExpiredMessage"
import { PasswordInput } from "../../molecules/PasswordInput"

import styles from "./ResetPasswordForm.module.scss"

export const ResetPasswordForm = () => {
  const [showErrorMessage, setShowErrorMessage] = useState(false)
  const [hasFeedback, setHasFeedback] = useState({
    password: false,
    confirmPassword: false
  })
  const { t } = useTranslation()
  const [form] = Form.useForm()
  const password = Form.useWatch("password", form)
  const services = useServices()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const token = searchParams.get("token")
  const { slug } = useParams()
  const context = useCreate(RESOURCES_NAMES.AUTHENTICATION.RESET_PASSWORD)

  if (context.isError) {
    return <ExpiredMessage />
  }

  const onFinish = async (value: { confirmPassword: string }) => {
    try {
      if (!token || !slug) {
        return navigate("/")
      }

      await context.mutateAsync({
        token,
        password,
        passwordConfirm: value.confirmPassword
      })

      navigate(`/${slug}/signin`, { replace: true })
    } catch (err: any) {
      services.logger.error(err)
    }
  }

  const loading = context.isPending

  return (
    <Form className={styles.wrap} form={form} onFinish={onFinish}>
      <Title className={styles.title} level={4}>
        {t("auth.setYourPassword")}
      </Title>
      <PasswordInput
        autoFocus
        hasFeedback={hasFeedback.password}
        validateTrigger={["onBlur"]}
        className={styles.itemForm}
        name="password"
        rules={[
          {
            required: true,
            message: t("error.passwordRequired") || "Required"
          },
          () => ({
            validator(_, value) {
              let errorMessage = t("error.passwordComplexity")

              if (typeof value === "string") {
                const regexSymbols = new RegExp(
                  "^(?=.*[a-z])(?=.*[A-Z])(?=.*\\d)(?=.*[-+_!@#$%^&*.,?]).+$"
                )
                const regexNumbers = new RegExp("(\\d)\\1{2,}")
                const weakPasswords = [
                  "password",
                  "p@ssw0rd",
                  "654321",
                  "qwe",
                  "1q2w3e",
                  "admin",
                  "monkey",
                  "dragon",
                  "letmein",
                  "welcome",
                  "iloveyou",
                  "asdfgh"
                ]

                const valueLowerCase = value.toLowerCase()
                const passwordIsWeak =
                  valueLowerCase.includes("abc") ||
                  valueLowerCase.includes("123") ||
                  weakPasswords.reduce(
                    (acc, weakPass) => acc || valueLowerCase.includes(weakPass),
                    false
                  ) ||
                  (typeof slug === "string" &&
                    valueLowerCase.includes(slug.toLowerCase()))

                const isMeetComplexityRequirements =
                  value.length >= 8 && regexSymbols.test(value)
                const noPatterns = !regexNumbers.test(value) && !passwordIsWeak

                if (isMeetComplexityRequirements && noPatterns) {
                  setShowErrorMessage(false)

                  return Promise.resolve()
                }

                if (isMeetComplexityRequirements && !noPatterns)
                  errorMessage = t("error.passwordPattern")
              }

              setShowErrorMessage(true)

              return Promise.reject(new Error(errorMessage))
            }
          })
        ]}
        placeholder={t("auth.newPassword") || ""}
        onBlur={() => setHasFeedback((prev) => ({ ...prev, password: true }))}
      />
      <PasswordInput
        hasFeedback={hasFeedback.confirmPassword}
        validateTrigger={["onBlur"]}
        className={styles.itemForm}
        name="confirmPassword"
        rules={[
          {
            required: true,
            message: t("error.passwordRequired") || "Required"
          },
          ({ getFieldValue }) => ({
            validator(_, value) {
              if (!value || getFieldValue("password") === value) {
                return Promise.resolve()
              }
              return Promise.reject(
                new Error(
                  t("error.passwordNotMatch") || "Passwords do not match"
                )
              )
            }
          })
        ]}
        placeholder={t("auth.confirmNewPassword") || ""}
        onBlur={() =>
          setHasFeedback((prev) => ({ ...prev, confirmPassword: true }))
        }
      />

      {showErrorMessage && (
        <div className={styles.explanationMessage}>
          <ul>
            <li>{t("auth.complexPassword")}</li>
            <li>{t("auth.symbolsPassword")}</li>
            <li>{t("auth.patternPassword")}</li>
          </ul>
        </div>
      )}

      <Button
        block
        disabled={loading}
        loading={loading}
        htmlType="submit"
        size="large"
        type="ks"
      >
        {t("general.save")}
      </Button>
    </Form>
  )
}
